import Link from 'next/link';
import FutureImage from '../../FutureImage';
import hillCountry from '../../../public/img/landing/hill-country.jpg';
import laosBoardwalk from '../../../public/img/landing/laos-boardwalk.jpg';
import wadiRum from '../../../public/img/landing/wadi-rum.jpg';
import { fraunces } from '../../Landing/fonts';
import {
  Accent,
  ArrowIcon,
  CtaButton,
  Eyebrow,
  GhostButton,
  INK,
  MUTED,
  PlaneIcon,
} from '../../Landing/ui';

// Same validated list as the landing Hero — these avatars exist.
const FEATURED_USERS = [
  'mibreit-photo',
  'x-rain',
  'for91days',
  'cocuyeras',
  'koenau',
  'juancar347',
  'adambarratt',
];

// The whole pitch in one picture: YOUR photos take the dashed flight route
// into a finished article, with the interview card in front — the tool does
// not start without both, and the visual should say so before the copy does.
export default function AiHero({ user, onJoin, userCount }) {
  const bloggerCount = userCount ? Math.floor(userCount / 1000) * 1000 : 4000;
  const rise = i => ({ animationDelay: `${0.08 * i}s` });

  return (
    <section className="tw-relative tw-overflow-hidden tw-pb-20 tw-pt-28 sm:tw-pt-32 lg:tw-pb-28 lg:tw-pt-36">
      {/* Atmosphere, matching the landing hero */}
      <div
        aria-hidden="true"
        className="tf-hero-glow tw-pointer-events-none tw-absolute -tw-right-40 -tw-top-40 tw-h-[600px] tw-w-[600px]"
      />
      <svg
        aria-hidden="true"
        className="tw-pointer-events-none tw-absolute tw-inset-0 tw-h-full tw-w-full tw-text-[#1D2D28] tw-opacity-[0.045] dark:tw-text-[#EBE6D9] dark:tw-opacity-[0.05]"
        preserveAspectRatio="xMidYMid slice"
        viewBox="0 0 1200 700"
      >
        <g fill="none" stroke="currentColor" strokeWidth="1.2">
          <path d="M-60 120c180-70 360 60 540-10s320-90 540-20 240 80 240 80" />
          <path d="M-60 180c180-70 360 60 540-10s320-90 540-20 240 80 240 80" />
          <path d="M-60 540c200-90 380 70 560 0s300-110 520-30 240 90 240 90" />
        </g>
      </svg>

      <div className="tw-relative tw-mx-auto tw-grid tw-max-w-7xl tw-grid-cols-1 tw-items-center tw-gap-16 tw-px-4 sm:tw-px-6 lg:tw-grid-cols-[1.02fr_0.98fr] lg:tw-gap-10 lg:tw-px-8">
        {/* Copy column */}
        <div>
          <div className="tf-rise" style={rise(0)}>
            <Eyebrow>AI Blogger · writes from your trip, or not at all</Eyebrow>
          </div>
          <h1
            style={rise(1)}
            className={`tf-rise ${fraunces.className} ${INK} tw-m-0 tw-text-[2.6rem] tw-font-semibold tw-leading-[1.06] tw-tracking-tight sm:tw-text-6xl lg:tw-text-[4rem]`}
          >
            The AI travel blog writer that{' '}
            <Accent>won&apos;t write without you</Accent>.
          </h1>
          <p
            style={rise(2)}
            className={`tf-rise ${MUTED} tw-mb-0 tw-mt-6 tw-max-w-xl tw-text-lg tw-leading-relaxed sm:tw-text-xl`}
          >
            AI Blogger starts with your photos. The real ones, with the times
            and places still on them. Then it interviews you about what a photo
            can&apos;t show: what you paid, what went wrong, what you&apos;d
            tell someone to skip. No photos, no article. Nothing invented, ever.
          </p>
          <div
            style={rise(3)}
            className="tf-rise tw-mt-9 tw-flex tw-flex-wrap tw-items-center tw-gap-4"
          >
            {user ? (
              <CtaButton href="/dashboard/aiblogger">
                Start with your photos
                <ArrowIcon />
              </CtaButton>
            ) : (
              <CtaButton onClick={onJoin}>
                Start with your photos
                <ArrowIcon />
              </CtaButton>
            )}
            <GhostButton href="#how">See what it asks you</GhostButton>
          </div>
          <p
            style={rise(4)}
            className={`tf-rise ${MUTED} tw-mb-0 tw-mt-5 tw-font-mono tw-text-xs tw-tracking-wide`}
          >
            Included with every plan · From $19/year · Monthly AI credits, top
            up anytime
          </p>
          <div
            style={rise(5)}
            className="tf-rise tw-mt-10 tw-flex tw-flex-wrap tw-items-center tw-gap-4"
          >
            <div className="tw-flex -tw-space-x-2.5">
              {FEATURED_USERS.map(featuredUser => (
                <Link
                  href={`/@${featuredUser}`}
                  prefetch={false}
                  key={featuredUser}
                  className="tw-block tw-rounded-full"
                >
                  <FutureImage
                    src={`/avatar/${featuredUser}/small`}
                    alt={`@${featuredUser} on TravelFeed`}
                    width={40}
                    height={40}
                    unoptimized
                    className="tw-h-10 tw-w-10 tw-rounded-full tw-border-2 tw-border-solid tw-border-[#FAF8F1] dark:tw-border-[#0E1A16] tw-object-cover tw-transition-transform hover:-tw-translate-y-1"
                  />
                </Link>
              ))}
            </div>
            <p className={`${MUTED} tw-m-0 tw-text-sm tw-leading-snug`}>
              <strong className={`${INK} tw-font-semibold`}>
                {bloggerCount.toLocaleString('en-US')}+ travel bloggers
              </strong>
              <br />
              write here already
            </p>
          </div>
        </div>

        {/* Transformation collage: field notes ✈ finished story */}
        <div className="tf-rise-card tw-relative tw-mx-auto tw-w-full tw-max-w-[480px] tw-pb-16 tw-pt-12 lg:tw-max-w-none">
          {/* Contact sheet of the reader's own photos, tucked behind */}
          <div className="tw-absolute tw-left-0 tw-top-0 tw-w-60 tw-rotate-[-6deg] tw-rounded-md tw-border tw-border-dashed tw-border-[#C9C0AA] dark:tw-border-[#3A4D44] [border-width:1.5px] tw-bg-[#FFFDF6] dark:tw-bg-[#1A2A24] tw-px-4 tw-pb-4 tw-pt-6 tw-shadow-[0_18px_36px_-18px_rgba(29,45,40,0.4)] sm:tw-w-64">
            {/* Tape strip */}
            <div
              aria-hidden="true"
              className="tw-absolute -tw-top-2.5 tw-left-1/2 tw-h-5 tw-w-16 -tw-translate-x-1/2 tw-rotate-2 tw-bg-[#EDE7D8]/80 dark:tw-bg-[#26382F]/80"
            />
            <p className="tw-m-0 tw-font-mono tw-text-[10px] tw-font-semibold tw-tracking-[0.22em] tw-text-[#8A958F]">
              YOUR CAMERA ROLL
            </p>
            <p className="tw-mb-0 tw-mt-1 tw-font-mono tw-text-[10px] tw-tracking-[0.14em] tw-text-[#8A958F]">
              15 SHOTS · GPS INTACT
            </p>
            <div className="tw-mt-3 tw-grid tw-grid-cols-3 tw-gap-1.5">
              {[
                [laosBoardwalk, 'A boardwalk through rice paddies in Laos'],
                [hillCountry, 'Tea terraces in the Sri Lankan hill country'],
                [wadiRum, 'Red sand and cliffs in Wadi Rum, Jordan'],
              ].map(([photo, alt]) => (
                <FutureImage
                  key={alt}
                  src={photo}
                  alt={alt}
                  width={72}
                  height={72}
                  sizes="72px"
                  className="tw-h-14 tw-w-full tw-rounded-sm tw-object-cover sm:tw-h-16"
                />
              ))}
            </div>
          </div>

          {/* Dashed flight route from notes to story */}
          <svg
            aria-hidden="true"
            className="tw-absolute tw-left-[34%] tw-top-[6%] tw-h-24 tw-w-44 tw-text-primary-600/60 dark:tw-text-primary-300/50"
            viewBox="0 0 180 100"
            fill="none"
          >
            <path
              d="M6 90 C 50 80, 90 30, 172 12"
              stroke="currentColor"
              strokeWidth="2"
              strokeDasharray="3 8"
              strokeLinecap="round"
            />
            <circle cx="6" cy="90" r="3.5" fill="currentColor" />
          </svg>
          <PlaneIcon className="tw-absolute tw-left-[58%] tw-top-[8%] tw-h-6 tw-w-6 tw-rotate-45 tw-text-primary-600 dark:tw-text-primary-300" />

          {/* The finished story */}
          <div className="tw-relative tw-z-10 tw-ml-auto tw-mt-24 tw-w-[88%] tw-rotate-[1.5deg] tw-rounded-2xl tw-border tw-border-solid tw-border-[#E7E0CF] dark:tw-border-[#26382F] tw-bg-white dark:tw-bg-[#15231E] tw-p-6 tw-shadow-[0_30px_60px_-22px_rgba(29,45,40,0.4)] tw-transition-transform tw-duration-500 hover:tw-rotate-[0.5deg] sm:tw-p-7">
            <div className="tw-flex tw-items-center tw-gap-2">
              <span className="tw-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-border tw-border-solid tw-border-[#E7E0CF] dark:tw-border-[#26382F] tw-px-2.5 tw-py-1">
                <img
                  src="/flags/la.svg"
                  alt=""
                  aria-hidden="true"
                  width={13}
                  height={13}
                  className="tw-h-[13px] tw-w-[13px]"
                />
                <span
                  className={`${INK} tw-text-[11px] tw-font-semibold tw-tracking-wide`}
                >
                  Vang Vieng
                </span>
              </span>
              <span className="tw-font-mono tw-text-[10px] tw-tracking-[0.2em] tw-text-[#8A958F]">
                DRAFT
              </span>
            </div>
            <p
              className={`${fraunces.className} ${INK} tw-mb-0 tw-mt-4 tw-text-xl tw-font-semibold tw-leading-snug tw-tracking-tight sm:tw-text-2xl`}
            >
              Three Days in Vang Vieng: Lagoons, Karsts and a Flat Tire
            </p>
            {/* Body being written */}
            <div className="tw-mt-5 tw-space-y-2.5">
              <div className="tw-h-2 tw-w-full tw-rounded tw-bg-[#1D2D28]/10 dark:tw-bg-[#EBE6D9]/15" />
              <div className="tw-h-2 tw-w-[94%] tw-rounded tw-bg-[#1D2D28]/10 dark:tw-bg-[#EBE6D9]/15" />
              <div className="tw-h-2 tw-w-full tw-rounded tw-bg-[#1D2D28]/10 dark:tw-bg-[#EBE6D9]/15" />
              <div className="tw-flex tw-items-center tw-gap-1">
                <div className="tw-h-2 tw-w-[55%] tw-rounded tw-bg-[#1D2D28]/10 dark:tw-bg-[#EBE6D9]/15" />
                <span className="tf-ai-caret tw-h-3.5 tw-w-[2px] tw-bg-primary-600 dark:tw-bg-primary-300" />
              </div>
            </div>
            {/* right-aligned so the interview card can overlap the corner */}
            <p className="tw-mb-0 tw-mt-5 tw-text-right tw-font-mono tw-text-[10px] tw-tracking-[0.18em] tw-text-[#8A958F]">
              YOUR PHOTOS · YOUR ANSWERS · NOTHING INVENTED
            </p>
          </div>

          {/* The interview: the part that needs a human */}
          <div className="tf-rise-chip tw-absolute tw-bottom-0 tw-left-0 tw-z-20 tw-w-[16rem] tw-rounded-xl tw-border tw-border-solid tw-border-[#E7E0CF] dark:tw-border-[#26382F] tw-bg-white dark:tw-bg-[#15231E] tw-px-4 tw-py-3.5 tw-shadow-[0_18px_40px_-16px_rgba(29,45,40,0.4)] sm:-tw-left-6 sm:tw-w-[17.5rem]">
            <p className="tw-m-0 tw-flex tw-items-center tw-gap-2 tw-font-mono tw-text-[10px] tw-font-semibold tw-tracking-[0.2em] tw-text-primary-700 dark:tw-text-primary-300">
              <span className="tw-relative tw-flex tw-h-2 tw-w-2">
                <span className="tw-absolute tw-inline-flex tw-h-full tw-w-full tw-animate-ping motion-reduce:tw-animate-none tw-rounded-full tw-bg-primary-400 tw-opacity-60" />
                <span className="tw-relative tw-inline-flex tw-h-2 tw-w-2 tw-rounded-full tw-bg-primary-500" />
              </span>
              QUESTION 4 OF 9
            </p>
            <p className={`${INK} tw-mb-0 tw-mt-2 tw-text-[13.5px] tw-leading-snug`}>
              What did a night cost, and in which currency?
            </p>
            <p className={`${MUTED} tw-mb-0 tw-mt-2 tw-font-mono tw-text-[11.5px]`}>
              55€ dorm bed, breakfast in
              <span className="tf-ai-caret tw-ml-0.5 tw-inline-block tw-h-3 tw-w-[2px] tw-translate-y-[2px] tw-bg-primary-600 dark:tw-bg-primary-300" />
            </p>
          </div>
        </div>
      </div>

      <style jsx global>{`
        .tf-hero-glow {
          background: radial-gradient(
            closest-side,
            rgba(38, 166, 154, 0.16),
            rgba(38, 166, 154, 0)
          );
        }
        .tw-dark .tf-hero-glow {
          background: radial-gradient(
            closest-side,
            rgba(0, 150, 136, 0.14),
            rgba(0, 150, 136, 0)
          );
        }
        @keyframes tf-rise {
          from {
            opacity: 0;
            transform: translateY(26px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes tf-rise-card {
          from {
            opacity: 0;
            transform: translateY(36px) rotate(1deg);
          }
          to {
            opacity: 1;
            transform: translateY(0) rotate(0deg);
          }
        }
        .tf-rise {
          animation: tf-rise 0.7s cubic-bezier(0.21, 0.65, 0.36, 1) both;
        }
        .tf-rise-card {
          animation: tf-rise-card 0.9s cubic-bezier(0.21, 0.65, 0.36, 1) 0.25s
            both;
        }
        .tf-rise-chip {
          animation: tf-rise 0.6s ease-out 0.85s both;
        }
        @keyframes tf-ai-caret {
          50% {
            opacity: 0;
          }
        }
        .tf-ai-caret {
          animation: tf-ai-caret 1.1s step-end infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .tf-rise,
          .tf-rise-card,
          .tf-rise-chip,
          .tf-ai-caret {
            animation: none;
          }
        }
      `}</style>
    </section>
  );
}
